import { format } from "date-fns";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useModal } from "hooks/useModal";
import { useReactTooltip } from "hooks/useReactTooltip";
import { useSchoolyear } from "hooks/useSchoolyear";

import { FontAwesomeIcon } from "components/FontAwesomeIcon";
import { SvgIconReact } from "components/SvgIconReact";
import { modalIds } from "constants/modals-constants";

import { CssIcon } from "styles/helpers/layout";
import type { ScheduledTestItemProps } from "./ScheduledTestItem.model";
import {
  InformationItemButton,
  StyledCollapseButton,
  StyledDateMetadata,
  StyledDetailedInformation,
  StyledEditButton,
  StyledGeneralInformation,
  StyledInfoRow,
  StyledInformationBlock,
  StyledInformationItem,
  StyledMetadataContainer,
  StyledProductFlag,
  StyledScheduledTestItem,
  StyledTestCode,
  StyledTestLevel,
  StyledTestName,
  StyledTimeMetadata,
} from "./ScheduledTestItem.styled";

export const ScheduledTestItem: React.FC<ScheduledTestItemProps> = ({ scheduledTest, onEdit }) => {
  const {
    id: scheduledTestId,
    cumulative,
    accessCodes,
    duration,
    extraTime,
    password,
    startDate: startDateString,
    testLevel,
    testType,
    title,
    productId,
    sebEnabled,
    schoolyearEnabled,
  } = scheduledTest;

  const { t } = useTranslation("scheduled-tests");
  const { t: tCommon } = useTranslation("common");
  const [collapsed, setCollapsed] = useState(true);
  const { toggleModal } = useModal();

  const { enableSchoolyearIntegration } = useFeatureFlags();
  const { fetchSchoolyearDashboard, loading: isSchoolyearDashboardUrlLoading } = useSchoolyear();
  const [schoolyearDashboardUrl, setSchoolyearDashboardUrl] = useState<string | null>(null);

  useReactTooltip(!collapsed);

  const startDate = new Date(startDateString);

  const onEditScheduledTest = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onEdit(scheduledTest);
  };

  const onRevealPasswordClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    toggleModal(modalIds.showPasswordModal, { password });
  };

  const onSchoolyearDashboardClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // Fetch dashboard url on demand only if it hasn't been loaded yet
    if (!schoolyearDashboardUrl) {
      fetchSchoolyearDashboard(scheduledTestId).then((obtainedDashboardUrl) => {
        setSchoolyearDashboardUrl(obtainedDashboardUrl);

        // Open dashboard in new tab if url was fetch successfully
        if (obtainedDashboardUrl) {
          window.open(obtainedDashboardUrl, "_blank");
        }
      });
    } else {
      // If dashboard url is already loaded, open it on new tab
      window.open(schoolyearDashboardUrl, "_blank");
    }
  };

  return (
    <StyledScheduledTestItem data-cy="scheduled-test-item">
      <StyledProductFlag productId={productId} />
      <StyledCollapseButton
        aria-label={
          collapsed ? tCommon("iconButtonLabel.expand") : tCommon("iconButtonLabel.collapse")
        }
        data-cy="scheduled-test-collapse-button"
        iconName={!collapsed ? "rightArrow" : "downArrow"}
        iconSize={1.5}
        onClick={() => setCollapsed(!collapsed)}
      />
      <StyledGeneralInformation>
        <StyledInfoRow>
          <StyledTestName data-cy="scheduled-test-name">{title}</StyledTestName>
          <StyledMetadataContainer>
            <StyledTestLevel data-cy="scheduled-test-level">
              <SvgIconReact iconName="graduationCapIcon" size={1.5} />
              {testLevel}
            </StyledTestLevel>
            <StyledDateMetadata data-cy="scheduled-test-date">
              <SvgIconReact iconName="calendarIcon" size={1.5} />
              {format(startDate, "dd MMM yyyy")}
            </StyledDateMetadata>
            <StyledTimeMetadata data-cy="scheduled-test-time">
              <SvgIconReact iconName="clockIcon" size={1.5} />
              <span data-cy="scheduled-test-time">{format(startDate, "HH:mm")}</span>
              {" | "}
              <span data-cy="scheduled-test-duration">{`${duration} min`}</span>
            </StyledTimeMetadata>
          </StyledMetadataContainer>
          <StyledTestCode data-cy="scheduled-test-code">
            <SvgIconReact iconName="codeIcon" size={2} />
            {accessCodes.standard}
          </StyledTestCode>
        </StyledInfoRow>
        {extraTime > 0 && (
          <StyledInfoRow>
            <StyledTestName>
              {t("scheduledTestList.item.extraTimeVersion", "Additional time version")}
            </StyledTestName>
            <StyledMetadataContainer>
              <StyledTimeMetadata data-cy="scheduled-test-extra-time">
                <SvgIconReact iconName="clockIcon" size={1.5} />
                {`+${extraTime} min`}
              </StyledTimeMetadata>
            </StyledMetadataContainer>
            <StyledTestCode data-cy="scheduled-test-extra-time-code">
              <SvgIconReact iconName="codeIcon" size={2} />
              {accessCodes.withExtraTime}
            </StyledTestCode>
          </StyledInfoRow>
        )}
      </StyledGeneralInformation>

      {!collapsed && (
        <StyledDetailedInformation>
          <StyledInformationBlock>
            <StyledInformationItem data-cy="scheduled-test-type">
              <CssIcon iconName="fas fa-check" iconSize="lg" />
              {testType === "summative-test"
                ? t("scheduledTestList.item.testType.summative", "Summative test")
                : t("scheduledTestList.item.testType.formative", "Formative test")}
            </StyledInformationItem>
            {password && (
              <StyledInformationItem data-cy="scheduled-test-password">
                <CssIcon iconName="fas fa-lock-alt" iconSize="lg" />
                <InformationItemButton
                  data-cy="scheduled-test-password-button"
                  data-tip={t("scheduledTestList.item.tooltip.password", "Click to view password")}
                  type="button"
                  onClick={onRevealPasswordClick}
                >
                  {t("scheduledTestList.item.hasPassword", "Password added")}
                </InformationItemButton>
              </StyledInformationItem>
            )}
            {cumulative && (
              <StyledInformationItem data-cy="scheduled-test-cumulative">
                <CssIcon iconName="fas fa-calculator" iconSize="lg" />
                {t("scheduledTestList.item.cumulative", "Cumulative Calculation")}
              </StyledInformationItem>
            )}
            {sebEnabled && (
              <StyledInformationItem data-cy="scheduled-test-seb-enabled">
                <CssIcon iconName="fas fa-shield-check" iconSize="lg" />
                {t("scheduledTestList.item.usesSEB", "SEB enabled")}
              </StyledInformationItem>
            )}
            {enableSchoolyearIntegration && schoolyearEnabled && (
              <StyledInformationItem data-cy="scheduled-test-schoolyear-enabled">
                <CssIcon iconName="fas fa-shield-check" iconSize="lg" />
                <InformationItemButton
                  data-cy="scheduled-test-schoolyear-dashboard-button"
                  data-tip={t(
                    "scheduledTestList.item.tooltip.schoolyear",
                    "Click to open dashboard",
                  )}
                  type="button"
                  disabled={isSchoolyearDashboardUrlLoading}
                  onClick={onSchoolyearDashboardClick}
                >
                  {t("scheduledTestList.item.usesSchoolyear", "Schoolyear enabled")}
                </InformationItemButton>
              </StyledInformationItem>
            )}
          </StyledInformationBlock>
          <StyledEditButton
            dataCy="scheduled-test-edit-button"
            variant="secondary"
            onClick={onEditScheduledTest}
          >
            <FontAwesomeIcon iconName="far fa-pencil" />
            {t("scheduledTestList.button.editTest", "Edit test")}
          </StyledEditButton>
        </StyledDetailedInformation>
      )}
    </StyledScheduledTestItem>
  );
};
